import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { pick } from "underscore";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import { ReferenceValue } from "../../../types/TenantInterfaces";
import { BrieGPTProject, Instruction } from "./useBrieGPTItems";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

interface Props {
  projectID: string;
  payload: {
    Name?: string;
    InstructionList?: Instruction[];
    newMembers?: ReferenceValue[];
    removedMembers?: string[];
  };
  onSuccess: (updatedProjectID: BrieGPTProject) => void;
  onError: (err: string) => void;
}

export default function useUpdateProject(tab: BrieGPTTabs) {
  const client = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom) || "";

  const updateProject = useMutation({
    mutationFn: ({ projectID, payload }: Props) => {
      return BrieConversationAPI.updateBrieGPTProject({
        projectID,
        payload,
      }).then((res) => res?.data?.data?.project);
    },
    onSuccess: async (project, { onSuccess, onError }) => {
      if (!project) {
        return onError("An error occurred while updating the project.");
      }

      const itemID = project.ViewID
        ? project.ViewID
        : project.Record
        ? project.Record.id
        : "";

      client.setQueryData(
        [tenantID, itemID, "BrieGPTProjects", tab],
        (prev: any) => {
          if (!prev) return prev;
          const projects = prev.records || [];
          const newProjects = projects.map((p: any) => {
            if (p.id === project.id) {
              p = {
                ...p,
                ...pick(project, ["Name", "InstructionList", "Members"]),
              };
            }

            return p;
          });

          return { ...prev, records: newProjects };
        }
      );
      onSuccess(project);
    },
    onError: (err, { onError }) => {
      onError(getErrorMessage(err));
    },
  });

  return {
    updateProject: updateProject.mutate,
    updating: updateProject.isPending,
  };
}
